import React, { useState, useEffect } from 'react';
import { fetchRegions, fetchCategories } from '../utils/api';
import { 
  getSelectedRegions, 
  saveSelectedRegions,
  getSelectedCategories,
  saveSelectedCategories,
  capitalizeFirstLetter,
  getCategoryClass,
  trackEvent,
  trackError
} from '../utils/helpers';

const SlideMenu = ({ isOpen, onClose, onRegionsChange, onCategoriesChange, onTotalRegionsLoaded, onCategoriesLoaded }) => {
  const [activeTab, setActiveTab] = useState('regions');
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState(getSelectedRegions());
  const [selectedCategories, setSelectedCategories] = useState(getSelectedCategories());
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);

  // Load regions and categories on mount - but only once
  useEffect(() => {
    // Prevent loading data multiple times
    if (hasLoadedData) return;
    
    const loadData = async () => {
      try {
        setHasLoadedData(true);
        const [regionsData, categoriesData] = await Promise.all([
          fetchRegions(),
          fetchCategories()
        ]);
        
        setRegions(regionsData || []);
        setCategories(categoriesData || []);
        
        // Initialize selections if empty (no filters = all selected)
        if (!isInitialized) {
          const storedRegions = getSelectedRegions();
          const storedCategories = getSelectedCategories();
          
          // If no regions were previously selected, select all
          if (storedRegions.length === 0 && regionsData && regionsData.length > 0) {
            setSelectedRegions(regionsData);
            // Don't save to localStorage yet - only save when user actually changes selection
          }
          
          // If no categories were previously selected, select all
          if (storedCategories.length === 0 && categoriesData && categoriesData.length > 0) {
            setSelectedCategories(categoriesData);
            // Don't save to localStorage yet - only save when user actually changes selection
          }
          
          setIsInitialized(true);
        }
        
        if (onTotalRegionsLoaded) {
          onTotalRegionsLoaded(regionsData ? regionsData.length : 22);
        }
        if (onCategoriesLoaded) {
          onCategoriesLoaded(categoriesData || []);
        }
      } catch (error) {
        console.error("Could not load data:", error);
        trackError('filter-data-load-error', error, {
          component: 'SlideMenu'
        });
        // Fallback to hardcoded data if API fails
        const fallbackRegions = [
          "Andalusien",
          "Aragonien",
          "Asturien",
          "Balearerna",
          "Baskien",
          "Ceuta",
          "Extremadura",
          "Galicien",
          "Kanarieöarna",
          "Kantabrien",
          "Kastilien-La Mancha",
          "Kastilien och León",
          "Katalonien",
          "La Rioja",
          "Madrid",
          "Melilla",
          "Murcia",
          "Navarra",
          "Valencia",
          "Spanien",
          "Internationellt"
        ];
        const fallbackCategories = [
          "nyheter",
          "sport",
          "ekonomi",
          "kultur",
          "vetenskap",
          "hälsa",
          "teknik",
          "miljö",
          "politik",
          "nöje",
          "resor",
          "mat & dryck"
        ];
        
        setRegions(fallbackRegions);
        setCategories(fallbackCategories);
        
        // Initialize selections if empty (no filters = all selected)
        if (!isInitialized) {
          const storedRegions = getSelectedRegions();
          const storedCategories = getSelectedCategories();
          
          // If no regions were previously selected, select all
          if (storedRegions.length === 0) {
            setSelectedRegions(fallbackRegions);
          }
          
          // If no categories were previously selected, select all
          if (storedCategories.length === 0) {
            setSelectedCategories(fallbackCategories);
          }
          
          setIsInitialized(true);
        }
        
        if (onTotalRegionsLoaded) {
          onTotalRegionsLoaded(fallbackRegions.length);
        }
        if (onCategoriesLoaded) {
          onCategoriesLoaded(fallbackCategories);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [hasLoadedData, onTotalRegionsLoaded, onCategoriesLoaded, isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle region selection change
  const handleRegionChange = (region) => {
    const wasSelected = selectedRegions.includes(region);
    const newSelectedRegions = wasSelected
      ? selectedRegions.filter(r => r !== region)
      : [...selectedRegions, region];
    
    trackEvent('filter-region-changed', {
      region,
      action: wasSelected ? 'deselected' : 'selected',
      totalSelected: newSelectedRegions.length,
      totalAvailable: regions.length
    });
    
    setSelectedRegions(newSelectedRegions);
    saveSelectedRegions(newSelectedRegions);
    
    // Notify parent component
    if (onRegionsChange) {
      onRegionsChange(newSelectedRegions);
    }
  };

  // Handle category selection change
  const handleCategoryChange = (category) => {
    const wasSelected = selectedCategories.includes(category);
    const newSelectedCategories = wasSelected
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category];
    
    trackEvent('filter-category-changed', {
      category,
      action: wasSelected ? 'deselected' : 'selected',
      totalSelected: newSelectedCategories.length,
      totalAvailable: categories.length
    });
    
    setSelectedCategories(newSelectedCategories);
    saveSelectedCategories(newSelectedCategories);
    
    // Notify parent component - send excluded categories
    if (onCategoriesChange) {
      // Convert selected to excluded (inverse logic for backend)
      const excludedCategories = categories.filter(c => !newSelectedCategories.includes(c));
      onCategoriesChange(excludedCategories);
    }
  };

  // Select all regions
  const handleSelectAllRegions = () => {
    trackEvent('filter-regions-select-all', { 
      totalSelected: regions.length,
      totalAvailable: regions.length
    });
    setSelectedRegions([...regions]);
    saveSelectedRegions([...regions]);
    if (onRegionsChange) {
      onRegionsChange([...regions]);
    }
  };

  // Clear all regions
  const handleClearAllRegions = () => {
    trackEvent('filter-regions-clear-all', {
      previousCount: selectedRegions.length
    });
    setSelectedRegions([]);
    saveSelectedRegions([]);
    if (onRegionsChange) {
      onRegionsChange([]);
    }
  };

  // Select all categories
  const handleSelectAllCategories = () => {
    trackEvent('filter-categories-select-all', { 
      totalSelected: categories.length,
      totalAvailable: categories.length
    });
    setSelectedCategories([...categories]);
    saveSelectedCategories([...categories]);
    if (onCategoriesChange) {
      onCategoriesChange([]); // No categories excluded
    }
  };

  // Clear all categories
  const handleClearAllCategories = () => {
    trackEvent('filter-categories-clear-all', {
      previousCount: selectedCategories.length
    });
    setSelectedCategories([]);
    saveSelectedCategories([]);
    if (onCategoriesChange) {
      onCategoriesChange([...categories]); // All categories excluded
    }
  };

  // Handle overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      trackEvent('menu-closed', { 
        method: 'overlay',
        regionsSelected: selectedRegions.length,
        categoriesSelected: selectedCategories.length
      });
      onClose();
    }
  };

  return (
    <div className={`slide-menu ${isOpen ? 'visible' : ''}`}>
      <div className="slide-menu-overlay" onClick={handleOverlayClick}></div>
      <div className="slide-menu-content">
        <div className="slide-menu-header">
          <h2>Filtrera artiklar</h2>
          <button 
            className="close-menu-button" 
            onClick={() => {
              trackEvent('menu-closed', { 
                method: 'button',
                regionsSelected: selectedRegions.length,
                categoriesSelected: selectedCategories.length
              });
              onClose();
            }}
            aria-label="Stäng meny"
          >
            ×
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'regions' ? 'active' : ''}`}
            onClick={() => {
              trackEvent('menu-tab-changed', { tab: 'regions' });
              setActiveTab('regions');
            }}
          >
            Regioner
            {selectedRegions.length > 0 && selectedRegions.length < regions.length && (
              <span className="tab-badge">{selectedRegions.length}</span>
            )}
          </button>
          <button
            className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => {
              trackEvent('menu-tab-changed', { tab: 'categories' });
              setActiveTab('categories');
            }}
          >
            Kategorier
            {selectedCategories.length > 0 && selectedCategories.length < categories.length && (
              <span className="tab-badge">{selectedCategories.length}</span>
            )}
          </button>
        </div>
        
        <div className="menu-section">
          {isLoading ? (
            <div className="loading-message">Laddar filter...</div>
          ) : (
            <>
              {/* Regions Tab Content */}
              {activeTab === 'regions' && (
                <>
                  <div className="filter-controls">
                    <button 
                      className="filter-control-button"
                      onClick={handleSelectAllRegions}
                      disabled={selectedRegions.length === regions.length}
                    >
                      Välj alla
                    </button>
                    <button 
                      className="filter-control-button"
                      onClick={handleClearAllRegions}
                      disabled={selectedRegions.length === 0}
                    >
                      Avmarkera alla
                    </button>
                  </div>
                  
                  <div className="filter-list">
                    {regions.map(region => (
                      <label key={region} className="filter-item">
                        <input
                          type="checkbox"
                          checked={selectedRegions.includes(region)}
                          onChange={() => handleRegionChange(region)}
                        />
                        <span className="filter-label">{region}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              
              {/* Categories Tab Content */}
              {activeTab === 'categories' && (
                <>
                  <div className="filter-controls">
                    <button 
                      className="filter-control-button"
                      onClick={handleSelectAllCategories}
                      disabled={selectedCategories.length === categories.length}
                    >
                      Välj alla
                    </button>
                    <button 
                      className="filter-control-button"
                      onClick={handleClearAllCategories}
                      disabled={selectedCategories.length === 0}
                    >
                      Avmarkera alla
                    </button>
                  </div>
                  
                  <div className="filter-list">
                    {categories.map(category => (
                      <label 
                        key={category} 
                        className={`filter-item category-item category-${getCategoryClass(category)}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(category)}
                          onChange={() => handleCategoryChange(category)}
                        />
                        <span className="filter-label">{capitalizeFirstLetter(category)}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
        
        <div className="menu-footer">
          <p className="filter-info">
            {activeTab === 'regions' ? (
              selectedRegions.length === 0 
                ? "Alla regioner visas" 
                : `${selectedRegions.length} av ${regions.length} regioner valda`
            ) : (
              selectedCategories.length === categories.length 
                ? "Alla kategorier visas" 
                : selectedCategories.length === 0
                ? "Inga kategorier valda"
                : `${selectedCategories.length} av ${categories.length} kategorier valda`
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SlideMenu;